import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import API from "../../api/api";
import AdminLayout from "../../components/admin/AdminLayout";

export default function ManageList() {
  const { id: tournamentId } = useParams();

  const [fighters, setFighters] = useState([]);
  const [loading, setLoading] = useState(true);

  const [genderFilter, setGenderFilter] = useState("All");
  const [paidFilter, setPaidFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClub, setSelectedClub] = useState("all");

  const [sortField, setSortField] = useState("name");
  const [sortAsc, setSortAsc] = useState(true);

  const [selectedFighters, setSelectedFighters] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [weightInputs, setWeightInputs] = useState({});
  const [weightBusyIds, setWeightBusyIds] = useState([]);
  const [approveBusyIds, setApproveBusyIds] = useState([]);
  const [paidBusyIds, setPaidBusyIds] = useState([]);
  const [deleteBusyIds, setDeleteBusyIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);

  const pageSize = 20;
  const UPLOADS_BASE = "https://warfc.com/api/uploads";

  const resolveImage = (path, type = "fighter") => {
    if (!path) return "";
    if (path.startsWith("http")) return path;

    let clean = path.replace(/^\/+/, "");
    if (clean.startsWith("uploads/")) clean = clean.replace(/^uploads\//, "");

    if (type === "club" && !clean.startsWith("clubs/")) {
      clean = `clubs/${clean}`;
    }

    return `${UPLOADS_BASE}/${clean}`;
  };

  useEffect(() => {
    if (tournamentId) loadFighters();
  }, [tournamentId]);

  const loadFighters = async () => {
    setLoading(true);
    try {
      const res = await API.get(`/tournaments/${tournamentId}/registration`);
      const data = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];

      const transformed = data.map((r) => ({
  registration_id: r.registration_id ?? r.id,
  fighter_id: r.fighter_id,

  name:
    `${r.name || ""} ${r.surname || ""}`.trim() ||
    "Нэргүй",

  image: resolveImage(
    r.fighter_image,
    "fighter"
  ),

  club:
    r.club_name ||
    r.club ||
    "-",

  club_logo: resolveImage(
    r.club_logo,
    "club"
  ),

  // FIX GENDER
  gender:
    r.registration_gender ||
    r.fighter_gender ||
    r.gender ||
    r.fighter?.gender ||
    "-",

  phone: r.phone || "-",

  approved: !!r.approved,

  status:
    r.approved
      ? "Approved"
      : "Pending",

  paid: !!r.paid,

  actual_weight:
    r.actual_weight !== null &&
    r.actual_weight !== undefined
      ? Number(r.actual_weight)
      : null,

  weight: r.weightclass_name
    ? `${r.weightclass_name}`
    : "-",

  age_division:
    r.age_division || "-",

  type:
    r.type ||
    r.registration_type ||
    "",

  division_id:
    r.division_id,

  division_weightclass_id:
    r.division_weightclass_id,

  status_value:
    r.status ||
    (r.approved
      ? "active"
      : "pending"),
}));

      setFighters(transformed);
      setSelectedFighters([]);
    } catch (err) {
      console.error("Failed to load fighters:", err);
    } finally {
      setLoading(false);
    }
  };

  const hasId = (list, id) => list.includes(id);

  const setBusyIn = (setter, id, busy) => {
    setter((prev) => {
      if (busy) return prev.includes(id) ? prev : [...prev, id];
      return prev.filter((value) => value !== id);
    });
  };

  const isWeightBusy = (id) => hasId(weightBusyIds, id);
  const isApproveBusy = (id) => hasId(approveBusyIds, id);
  const isPaidBusy = (id) => hasId(paidBusyIds, id);
  const isDeleteBusy = (id) => hasId(deleteBusyIds, id);

  const isAnyRowBusy = (id) =>
    isWeightBusy(id) ||
    isApproveBusy(id) ||
    isPaidBusy(id) ||
    isDeleteBusy(id);

  // Sorting state only. Visible rows are derived below with useMemo.
  const sortBy = (field) => {
    setSortAsc((prevAsc) => (sortField === field ? !prevAsc : true));
    setSortField(field);
  };

  // Single source of truth: fighters.
  // Filtering + searching + sorting are always recalculated from fighters,
  // so actions never need to manually keep a second filtered state in sync.
  const filtered = useMemo(() => {
    let data = [...fighters];

    if (genderFilter !== "All") {
      data = data.filter((f) => {
        const value = String(f.gender || "").toLowerCase().trim();

        if (genderFilter === "Male") {
          return ["male", "m", "эрэгтэй", "эр", "man", "boy"].includes(value);
        }

        if (genderFilter === "Female") {
          return ["female", "f", "эмэгтэй", "эм", "woman", "girl"].includes(value);
        }

        return true;
      });
    }

    if (selectedClub !== "all") {
      data = data.filter((f) => String(f.club || "") === selectedClub);
    }

    if (paidFilter === "Paid") {
      data = data.filter((f) => !!f.paid);
    } else if (paidFilter === "Unpaid") {
      data = data.filter((f) => !f.paid);
    }

    if (typeFilter !== "All") {
      data = data.filter(
        (f) => String(f.type || "").toLowerCase() === typeFilter.toLowerCase()
      );
    }

    const query = String(searchTerm || "").trim().toLowerCase();
    if (query) {
      data = data.filter((f) =>
        [f.name, f.phone, f.club, f.weight, f.age_division]
          .map((value) => String(value ?? "").toLowerCase())
          .some((value) => value.includes(query))
      );
    }

    const normalizeSortValue = (value) => {
      if (value === null || value === undefined) return "";
      if (typeof value === "boolean") return value ? 1 : 0;
      if (typeof value === "number") return value;
      return String(value).toLowerCase();
    };

    data.sort((a, b) => {
      const av = normalizeSortValue(a?.[sortField]);
      const bv = normalizeSortValue(b?.[sortField]);

      if (av > bv) return sortAsc ? 1 : -1;
      if (av < bv) return sortAsc ? -1 : 1;
      return 0;
    });

    return data;
  }, [
    fighters,
    genderFilter,
    paidFilter,
    typeFilter,
    selectedClub,
    searchTerm,
    sortField,
    sortAsc,
  ]);

  const resetFilters = () => {
    setGenderFilter("All");
    setPaidFilter("All");
    setTypeFilter("All");
    setSearchTerm("");
    setSelectedClub("all");
    setCurrentPage(1);
  };

  const hasActiveFilters =
    genderFilter !== "All" ||
    paidFilter !== "All" ||
    typeFilter !== "All" ||
    selectedClub !== "all" ||
    String(searchTerm || "").trim() !== "";

  useEffect(() => {
    setCurrentPage(1);
  }, [genderFilter, paidFilter, typeFilter, selectedClub, searchTerm]);

  // Selection
  const toggleSelect = (id) => {
    setSelectedFighters((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // Bulk approve — optimistic UI, NO REFRESH
  const approveBulk = async () => {
    if (!selectedFighters.length || bulkBusy) return;

    const ids = [...selectedFighters];
    const beforeById = new Map(
      fighters
        .filter((f) => ids.includes(f.registration_id))
        .map((f) => [f.registration_id, { approved: f.approved, status: f.status }])
    );

    setBulkBusy(true);
    setFighters((prev) =>
      prev.map((f) =>
        ids.includes(f.registration_id)
          ? { ...f, approved: true, status: "Approved" }
          : f
      )
    );
    setSelectedFighters([]);

    try {
      await API.post(
        `/tournaments/${tournamentId}/registration/approve-bulk`,
        { ids }
      );
    } catch (err) {
      console.error("Bulk approve error:", err);
      setFighters((prev) =>
        prev.map((f) => {
          const before = beforeById.get(f.registration_id);
          return before ? { ...f, ...before } : f;
        })
      );
      setSelectedFighters(ids);
      alert("Тамирчин батлахад алдаа гарлаа.");
    } finally {
      setBulkBusy(false);
    }
  };

  // Remove — optimistic UI, no refresh, partial-failure safe
  const remove = async (ids) => {
    if (!ids.length || bulkBusy) return;
    if (!window.confirm("Сонгосон тамирчдыг тэмцээнээс бүртгэл цуцлах уу?")) return;

    const uniqueIds = [...new Set(ids)];
    const removedRows = fighters.filter((f) =>
      uniqueIds.includes(f.registration_id)
    );

    setBulkBusy(uniqueIds.length > 1);
    setDeleteBusyIds((prev) => [...new Set([...prev, ...uniqueIds])]);
    setFighters((prev) =>
      prev.filter((f) => !uniqueIds.includes(f.registration_id))
    );
    setSelectedFighters((prev) =>
      prev.filter((id) => !uniqueIds.includes(id))
    );

    const results = await Promise.allSettled(
      uniqueIds.map((id) =>
        API.delete(`/tournaments/${tournamentId}/registration/${id}`)
      )
    );

    const failedIds = uniqueIds.filter(
      (_, index) => results[index].status === "rejected"
    );

    if (failedIds.length) {
      const failedRows = removedRows.filter((f) =>
        failedIds.includes(f.registration_id)
      );

      // Restore only failed deletions. Successful deletions stay removed.
      setFighters((prev) => {
        const existingIds = new Set(prev.map((f) => f.registration_id));
        return [
          ...prev,
          ...failedRows.filter((f) => !existingIds.has(f.registration_id)),
        ];
      });
      setSelectedFighters((prev) => [...new Set([...prev, ...failedIds])]);

      alert(
        `${failedIds.length} тамирчныг устгаж чадсангүй. Амжилттай устсан бусад тамирчид хэвээр устсан.`
      );
    }

    setDeleteBusyIds((prev) =>
      prev.filter((id) => !uniqueIds.includes(id))
    );
    setBulkBusy(false);
  };

  // Toggle paid — optimistic UI, NO REFRESH
  const togglePaid = async (id, paid) => {
    if (isPaidBusy(id) || isDeleteBusy(id)) return;

    const previousPaid =
      fighters.find((f) => f.registration_id === id)?.paid ?? !paid;

    setBusyIn(setPaidBusyIds, id, true);
    setFighters((prev) =>
      prev.map((f) =>
        f.registration_id === id ? { ...f, paid } : f
      )
    );

    try {
      await API.post(
        `/tournaments/${tournamentId}/registration/${id}/paid`,
        { paid }
      );
    } catch (err) {
      console.error("Paid toggle error:", err);
      setFighters((prev) =>
        prev.map((f) =>
          f.registration_id === id ? { ...f, paid: previousPaid } : f
        )
      );
      alert("Төлбөрийн төлөв шинэчлэхэд алдаа гарлаа.");
    } finally {
      setBusyIn(setPaidBusyIds, id, false);
    }
  };

// ==========================
// UPDATE ACTUAL WEIGHT
// ==========================

  const updateActualWeight = async (id, actualWeight) => {
    if (isWeightBusy(id) || isDeleteBusy(id)) return;

    const value = actualWeight === "" ? null : Number(actualWeight);

    if (
      value !== null &&
      (!Number.isFinite(value) || value <= 0 || value > 300)
    ) {
      alert("Please enter a valid weight.");
      return;
    }

    const fighter = fighters.find((f) => f.registration_id === id);
    if (!fighter) return;

    const previousWeight = fighter.actual_weight ?? null;

    // Do not call API when the value did not actually change.
    const sameValue =
      (previousWeight === null && value === null) ||
      (previousWeight !== null &&
        value !== null &&
        Number(previousWeight) === Number(value));

    if (sameValue) return;

    setBusyIn(setWeightBusyIds, id, true);
    setFighters((prev) =>
      prev.map((f) =>
        f.registration_id === id ? { ...f, actual_weight: value } : f
      )
    );

    try {
      await API.patch(`/tournaments/registration/${id}/actual-weight`, {
        actual_weight: value,
      });
    } catch (err) {
      console.error("Actual weight update error:", err);
      setFighters((prev) =>
        prev.map((f) =>
          f.registration_id === id
            ? { ...f, actual_weight: previousWeight }
            : f
        )
      );
      alert(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to update actual weight."
      );
    } finally {
      setBusyIn(setWeightBusyIds, id, false);
    }
  };

  // Toggle approve — optimistic UI, NO REFRESH.
  // Approval uses its own busy state so clicking approve while the
  // Actual Weight input is focused does not get swallowed by input onBlur.
  const toggleApprove = async (id) => {
    if (isApproveBusy(id) || isDeleteBusy(id)) return;

    const fighter = fighters.find((f) => f.registration_id === id);
    if (!fighter) return;

    const nextApproved = !fighter.approved;

    setBusyIn(setApproveBusyIds, id, true);
    setFighters((prev) =>
      prev.map((f) =>
        f.registration_id === id
          ? {
              ...f,
              approved: nextApproved,
              status: nextApproved ? "Approved" : "Pending",
            }
          : f
      )
    );

    try {
      const res = await API.post(
        `/tournaments/${tournamentId}/registration/${id}/approve`
      );

      const backendApproved =
        res?.approved ?? res?.data?.approved ?? nextApproved;

      setFighters((prev) =>
        prev.map((f) =>
          f.registration_id === id
            ? {
                ...f,
                approved: backendApproved,
                status: backendApproved ? "Approved" : "Pending",
              }
            : f
        )
      );
    } catch (err) {
      console.error("Approve toggle error:", err);
      setFighters((prev) =>
        prev.map((f) =>
          f.registration_id === id
            ? {
                ...f,
                approved: fighter.approved,
                status: fighter.status,
              }
            : f
        )
      );
      alert("Баталгаажуулах үед алдаа гарлаа.");
    } finally {
      setBusyIn(setApproveBusyIds, id, false);
    }
  };

  // Bulk mark paid — partial-failure safe, NO REFRESH
  const markPaidBulk = async () => {
    if (!selectedFighters.length || bulkBusy) return;

    const ids = [...selectedFighters];
    const previousPaidById = new Map(
      fighters
        .filter((f) => ids.includes(f.registration_id))
        .map((f) => [f.registration_id, f.paid])
    );

    setBulkBusy(true);
    setPaidBusyIds((prev) => [...new Set([...prev, ...ids])]);
    setFighters((prev) =>
      prev.map((f) =>
        ids.includes(f.registration_id) ? { ...f, paid: true } : f
      )
    );
    setSelectedFighters([]);

    const results = await Promise.allSettled(
      ids.map((id) =>
        API.post(`/tournaments/${tournamentId}/registration/${id}/paid`, {
          paid: true,
        })
      )
    );

    const failedIds = ids.filter(
      (_, index) => results[index].status === "rejected"
    );

    if (failedIds.length) {
      setFighters((prev) =>
        prev.map((f) =>
          failedIds.includes(f.registration_id)
            ? {
                ...f,
                paid: previousPaidById.get(f.registration_id),
              }
            : f
        )
      );
      setSelectedFighters(failedIds);
      alert(
        `${failedIds.length} тамирчны төлбөрийн төлөв шинэчлэгдсэнгүй. Бусад нь амжилттай хадгалагдлаа.`
      );
    }

    setPaidBusyIds((prev) => prev.filter((id) => !ids.includes(id)));
    setBulkBusy(false);
  };

  // Pagination
  const totalPages = Math.ceil(filtered.length / pageSize);

  useEffect(() => {
    const maxPage = Math.max(1, totalPages);
    if (currentPage > maxPage) setCurrentPage(maxPage);
  }, [currentPage, totalPages]);

  const paginated = filtered.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const currentPageIds = paginated.map((f) => f.registration_id);
  const allCurrentPageSelected =
    currentPageIds.length > 0 &&
    currentPageIds.every((id) => selectedFighters.includes(id));

  const toggleSelectAll = () => {
    if (!currentPageIds.length) return;

    setSelectedFighters((prev) => {
      if (allCurrentPageSelected) {
        return prev.filter((id) => !currentPageIds.includes(id));
      }

      return [...new Set([...prev, ...currentPageIds])];
    });
  };

  // ==========================
// CLUB LIST
// ==========================

const clubs = [...new Set(
  [
    ...fighters.map((f) => f.club),
    selectedClub !== "all" ? selectedClub : null,
  ].filter((club) => club && club !== "-")
)].sort((a, b) => a.localeCompare(b));

  // Stats
  const totalFighters = fighters.length;
const totalMale = fighters.filter((f) => {
  const gender = String(
    f.gender || ""
  )
    .toLowerCase()
    .trim();

  return [
    "male",
    "m",
    "эрэгтэй",
    "эр",
    "man",
    "boy",
  ].includes(gender);
}).length;


const totalFemale = fighters.filter((f) => {
  const gender = String(
    f.gender || ""
  )
    .toLowerCase()
    .trim();

  return [
    "female",
    "f",
    "эмэгтэй",
    "эм",
    "woman",
    "girl",
  ].includes(gender);
}).length;

 const totalAltanGaraa = fighters.filter(
    f => (f.type || "").toLowerCase() === "altan_garaa"
  ).length;
  const totalMain = fighters.filter(
    f => (f.type || "").toLowerCase() === "main"
  ).length;

  // ==========================
// SELECTED CLUB COUNT
// ==========================

const selectedClubCount =
  selectedClub === "all"
    ? fighters.length
    : fighters.filter(
        (f) => f.club === selectedClub
      ).length;

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6 text-white font-bold animate-pulse">Мэдээлэл ачааллаж байна...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 text-white">
        <h1 className="text-3xl font-bold mb-4">Бүртгэлтэй Тамирчид</h1>

        {/* Stats Bar */}
        <div className="mb-4 p-4 bg-gray-800 rounded-lg flex flex-wrap gap-6 text-sm font-semibold border border-gray-700">
          <div>Нийт тамирчид: <span className="text-yellow-400 font-mono text-base">{totalFighters}</span></div>
          <div>Эрэгтэй: <span className="text-blue-400 font-mono text-base">{totalMale}</span></div>
          <div>Эмэгтэй: <span className="text-pink-400 font-mono text-base">{totalFemale}</span></div>
          <div>Үндсэн: <span className="text-cyan-400 font-mono text-base">{totalMain}</span></div>
          <div>Алтан Гараа: <span className="text-amber-400 font-mono text-base">{totalAltanGaraa}</span></div>
        </div>

        {/* Controls / Filters */}
        <div className="flex flex-wrap gap-3 mb-4 items-center justify-between">
          <div className="flex flex-wrap gap-3">
            <select
              className="p-2 bg-gray-700 rounded text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              value={genderFilter}
              onChange={(e) => setGenderFilter(e.target.value)}
            >
              <option value="All">Бүх хүйс</option>
              <option value="Male">Эрэгтэй</option>
              <option value="Female">Эмэгтэй</option>
            </select>

{/* CLUB FILTER */}

<select
  className="p-2 bg-gray-700 rounded text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 min-w-[180px]"
  value={selectedClub}
  onChange={(e) => setSelectedClub(e.target.value)}
>
  <option value="all">
    Бүх клуб ({fighters.length})
  </option>

  {clubs.map((club) => {

    const count =
      fighters.filter(
        (f) => f.club === club
      ).length;

    return (
      <option
        key={club}
        value={club}
      >
        {club} ({count})
      </option>
    );

  })}

</select>

            <select
              className="p-2 bg-gray-700 rounded text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="All">Бүх төрөл</option>
              <option value="main">Үндсэн</option>
              <option value="altan_garaa">Алтан Гараа</option>
            </select>

            <select
              className="p-2 bg-gray-700 rounded text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              value={paidFilter}
              onChange={(e) => setPaidFilter(e.target.value)}
            >
              <option value="All">Төлбөрийн төлөв (Бүгд)</option>
              <option value="Paid">Төлсөн</option>
              <option value="Unpaid">Төлөөгүй</option>
            </select>

            <input
              className="p-2 bg-gray-700 rounded w-64 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              placeholder="Нэр, утас, клубээр хайх..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <button
              type="button"
              onClick={resetFilters}
              disabled={!hasActiveFilters}
              className="px-3 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed rounded text-xs font-bold transition-all"
            >
              Шүүлтүүр цэвэрлэх
            </button>
          </div>

          {/* Bulk Actions */}
          <div className="flex flex-wrap gap-2">
            <button
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 rounded text-xs font-bold uppercase transition-all"
              disabled={!selectedFighters.length || bulkBusy}
              onClick={markPaidBulk}
            >
              {bulkBusy ? "Хадгалж байна..." : "Төлбөр төлсөн"}
            </button>

            <button
              className="px-3 py-1.5 bg-green-600 hover:bg-green-500 disabled:opacity-40 rounded text-xs font-bold uppercase transition-all"
              disabled={!selectedFighters.length || bulkBusy}
              onClick={approveBulk}
            >
              {bulkBusy ? "Хадгалж байна..." : "Батлах"}
            </button>

            <button
              className="px-3 py-1.5 bg-red-600 hover:bg-red-500 disabled:opacity-40 rounded text-xs font-bold uppercase transition-all"
              disabled={!selectedFighters.length || bulkBusy}
              onClick={() => remove(selectedFighters)}
            >
              {bulkBusy ? "Хадгалж байна..." : "Устгах"}
            </button>
          </div>
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-gray-400">
          <span>Харагдаж буй: <b className="text-white">{filtered.length}</b></span>
          {selectedClub !== "all" && (
            <span className="rounded bg-gray-800 border border-gray-700 px-2 py-1">
              {selectedClub}: <b className="text-amber-300">{selectedClubCount}</b>
            </span>
          )}
          {selectedFighters.length > 0 && (
            <span className="rounded bg-blue-900/30 border border-blue-700/40 px-2 py-1 text-blue-200">
              Сонгосон: {selectedFighters.length}
            </span>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-lg border border-gray-700">
          <table className="min-w-full bg-gray-800 text-left text-sm">
            <thead>
              <tr className="bg-gray-700 border-b border-gray-600 text-gray-300">
                {/* NUMBER */}

  <th className="p-3 w-12 text-center">
    #
  </th>

  {/* CHECKBOX */}

  <th className="p-3 w-10 text-center">

    <input
      type="checkbox"
      checked={allCurrentPageSelected}
      onChange={toggleSelectAll}
      title="Зөвхөн энэ хуудсын тамирчдыг сонгоно"
    />

  </th>
                <th className="p-3 cursor-pointer select-none" onClick={() => sortBy("name")}>Нэр</th>
                <th className="p-3 cursor-pointer select-none" onClick={() => sortBy("club")}>Клуб</th>
                <th className="p-3 cursor-pointer select-none" onClick={() => sortBy("gender")}>Хүйс</th>
                <th className="p-3 cursor-pointer select-none" onClick={() => sortBy("phone")}>Утас</th>
                <th className="p-3 cursor-pointer select-none" onClick={() => sortBy("type")}>Төрөл</th>
                <th className="p-3 cursor-pointer select-none" onClick={() => sortBy("weight")}>Жин</th>
                <th className="p-3 cursor-pointer select-none" onClick={() => sortBy("age_division")}>Насны ангилал</th>
                <th className="p-3 cursor-pointer select-none" onClick={() => sortBy("paid")}>Төлбөр</th>
                <th className="p-3 cursor-pointer select-none" onClick={() => sortBy("actual_weight")}>R Jin</th>
                <th className="p-3 cursor-pointer select-none" onClick={() => sortBy("status")}>Төлөв</th>
                <th className="p-3 text-center">Үйлдэл</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-700">
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan="13" className="p-6 text-center text-gray-400">
                    Мэдээлэл олдсонгүй.
                  </td>
                </tr>
              ) : (
                paginated.map((f, index) => (
                  <tr key={f.registration_id} className="hover:bg-gray-750 transition-colors">

  {/* Number */}
  <td className="p-3 text-center text-gray-400 font-mono">
    {(currentPage - 1) * pageSize + index + 1}
  </td>

  {/* Checkbox */}
  <td className="p-3 text-center">
                      <input
                        type="checkbox"
                        checked={selectedFighters.includes(f.registration_id)}
                        disabled={isDeleteBusy(f.registration_id)}
                        onChange={() => toggleSelect(f.registration_id)}
                      />
                    </td>

                    {/* 2. Name */}
                    <td className="p-3 font-semibold text-white">
                      {f.name}
                    </td>

                    {/* 3. Club */}
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        {f.club_logo && (
                          <img
                            src={f.club_logo}
                            alt={f.club}
                            className="w-5 h-5 object-contain bg-white rounded-full p-0.5"
                            onError={(e) => (e.currentTarget.style.display = "none")}
                          />
                        )}
                        <span>{f.club}</span>
                      </div>
                    </td>

                    {/* 4. Gender */}
                    <td className="p-3">
  <span
    className={
      ["female", "f", "эмэгтэй", "эм", "woman", "girl"].includes(
        String(f.gender || "").toLowerCase().trim()
      )
        ? "font-bold text-pink-400"
        : "font-bold text-blue-400"
    }
  >
    {f.gender}
  </span>
</td>

                    {/* 5. Phone */}
                    <td className="p-3 font-mono text-gray-300">{f.phone}</td>

                    {/* 6. Type */}
                    <td className="p-3">
                      <span
                        className={`inline-flex px-2.5 py-1 rounded text-xs font-bold ${
                          (f.type || "").toLowerCase() === "altan_garaa"
                            ? "bg-amber-600/20 text-amber-300 border border-amber-500/30"
                            : "bg-cyan-600/20 text-cyan-300 border border-cyan-500/30"
                        }`}
                      >
                        {(f.type || "").toLowerCase() === "altan_garaa"
                          ? "Алтан Гараа"
                          : "Үндсэн"}
                      </span>
                    </td>

                    {/* 7. Weight */}
                    <td className="p-3">{f.weight}</td>

                    {/* 8. Age Division */}
                    <td className="p-3">{f.age_division}</td>

                    {/* 9. Paid */}
<td className="p-3">
  <button
    className={`px-2.5 py-1 rounded text-xs font-bold transition-all ${
      f.paid
        ? "bg-emerald-600 hover:bg-emerald-500"
        : "bg-amber-600 hover:bg-amber-500"
    }`}
    disabled={isPaidBusy(f.registration_id) || isDeleteBusy(f.registration_id)}
    onClick={() => togglePaid(f.registration_id, !f.paid)}
  >
    {isPaidBusy(f.registration_id) ? "..." : f.paid ? "Төлсөн" : "Төлөөгүй"}
  </button>
</td>

{/* 10. Actual Weight */}
<td className="p-3">
  <input
    type="number"
    step="0.1"
    min="1"
    max="300"
    value={
      weightInputs[f.registration_id] !== undefined
        ? weightInputs[f.registration_id]
        : f.actual_weight ?? ""
    }
    onChange={(e) => {
      const value = e.target.value;

      // ONLY update the input while typing
      setWeightInputs((prev) => ({
        ...prev,
        [f.registration_id]: value,
      }));
    }}
    onBlur={async () => {
      const value =
        weightInputs[f.registration_id] ??
        f.actual_weight ??
        "";

      await updateActualWeight(
        f.registration_id,
        value
      );

      // Clear temporary input value
      setWeightInputs((prev) => {
        const next = { ...prev };
        delete next[f.registration_id];
        return next;
      });
    }}
    onKeyDown={(e) => {
      if (e.key === "Enter") {
        e.currentTarget.blur();
      }
    }}
    disabled={isWeightBusy(f.registration_id) || isDeleteBusy(f.registration_id)}
    placeholder="kg"
    className="w-20 disabled:opacity-50 bg-gray-800 border border-gray-600 rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-yellow-500"
  />
</td>

{/* 11. Status */}
<td className="p-3">
                      <button
                        className={`px-2.5 py-1 rounded text-xs font-bold transition-all ${
                          f.approved ? "bg-emerald-600 hover:bg-emerald-500" : "bg-amber-600 hover:bg-amber-500"
                        }`}
                        disabled={
                          isApproveBusy(f.registration_id) ||
                          isDeleteBusy(f.registration_id)
                        }
                        onClick={() => toggleApprove(f.registration_id)}
                      >
                        {isApproveBusy(f.registration_id)
                          ? "..."
                          : f.approved
                          ? "Батлагдсан"
                          : "Хүлээгдэж буй"}
                      </button>
                    </td>
 
                    {/* 11. Actions */}
                    <td className="p-3 text-center">
                      <button
                        className="px-2.5 py-1 bg-red-600/80 hover:bg-red-600 disabled:opacity-40 rounded text-xs font-bold transition-all"
                        disabled={isAnyRowBusy(f.registration_id) || bulkBusy}
                        onClick={() => remove([f.registration_id])}
                      >
                        {isDeleteBusy(f.registration_id) ? "..." : "Устгах"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="mt-4 flex items-center justify-between text-sm text-gray-400">
          <div>
            Нийт {filtered.length} үр дүнгээс {filtered.length === 0 ? 0 : (currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, filtered.length)} харуулж байна
          </div>
          <div className="flex gap-2">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="px-3 py-1 bg-gray-700 hover:bg-gray-600 disabled:opacity-30 rounded transition-all"
            >
              Өмнөх
            </button>
            <span className="px-3 py-1 font-mono text-white">
              {currentPage} / {totalPages || 1}
            </span>
            <button
              disabled={currentPage === totalPages || totalPages === 0}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="px-3 py-1 bg-gray-700 hover:bg-gray-600 disabled:opacity-30 rounded transition-all"
            >
              Дараах
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}